/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, Heading, HFlow, Icon, Modal, ModalBody, Text, VFlow } from 'bold-ui'
import AccordionList from 'components/accordion/AccordionList'
import { DateTime } from 'components/date'
import { HLabel } from 'components/HLabel'
import { StickyButton } from 'components/sticky-button/StickyButton'
import theme from 'config/theme'
import { usePeriogramasCompletoQuery } from 'graphql/hooks.generated'
import { AtendimentoProfissionalOdonto } from 'graphql/types.generated'
import { Fragment, useCallback, useMemo, useState } from 'react'

import PeriogramaCompletoTableView from '../periograma-completo-table-view/PeriogramaCompletoTableView'
import {
  calculaPipsPeriogramaCompleto,
  calculaPorcentagensPeriogramaCompleto,
} from '../periograma-completo-table/periogramaCompletoCalculos'
import { PeriogramaCompletoTableModel } from '../periograma-completo-table/PeriogramaCompletoTable'
import { convertPeriogramaCompletoQueryToModel } from './converter'

export interface HistoricoPeriogramaCompletoItemModel {
  id: ID
  atendProfOdonto: AtendimentoProfissionalOdonto
  periogramaCompleto: PeriogramaCompletoTableModel
  observacao?: string
}

interface HistoricoPeriogramaCompletoViewProps {
  prontuarioId: ID
}

export const HistoricoPeriogramaCompletoView = (props: HistoricoPeriogramaCompletoViewProps) => {
  const { prontuarioId } = props

  const [modalOpen, setModalOpen] = useState(false)
  const [hasItemExpanded, setHasItemExpanded] = useState(false)
  const [ref, setRef] = useState<HTMLDivElement>()

  const {
    data: { periogramasCompleto },
  } = usePeriogramasCompletoQuery({ variables: { input: { prontuarioId } } })

  const data = useMemo(() => convertPeriogramaCompletoQueryToModel(periogramasCompleto), [periogramasCompleto])

  const handleAccordionChange = useCallback(
    (items: string[]) => {
      setHasItemExpanded(items.length > 0)

      if (ref?.scrollTop > 0) {
        setTimeout(() => {
          const accordionExpanded: HTMLElement = ref.querySelector(
            '*[data-accordion-component="Accordion"] *[data-accordion-component="AccordionItemButton"][aria-expanded="true"] '
          )

          ref?.scrollTo({ top: accordionExpanded?.offsetTop - 16 })
        })
      }
    },
    [ref]
  )

  const collapseAccordion = useCallback(() => {
    const accordionExpanded: HTMLElement = ref.querySelector(
      '*[data-accordion-component="Accordion"] *[data-accordion-component="AccordionItemButton"][aria-expanded="true"] '
    )

    accordionExpanded?.click()
  }, [ref])

  return (
    <Fragment>
      <Button kind='primary' size='small' onClick={() => setModalOpen(true)} disabled={!(data?.length > 0)}>
        <Icon
          icon='zoomOutline'
          style={css`
            margin-right: 0.5rem;
          `}
        />
        Visualizar histórico
      </Button>

      <Modal
        open={modalOpen}
        size='large'
        onClose={() => setModalOpen(false)}
        style={css`
          width: 58.5rem;
          max-height: 95vh;
          position: relative;
        `}
        containerRef={setRef}
      >
        <ModalBody>
          <VFlow vSpacing={1.5}>
            <Heading level={1}>Histórico de periograma completo</Heading>

            <AccordionList<HistoricoPeriogramaCompletoItemModel>
              onAccordionChange={handleAccordionChange}
              data={{
                content: data,
                pageInfo: {},
              }}
              paginate={false}
              style={classes.accordion}
              allowMultipleExpanded={false}
              accordionItemHeader={(item) => {
                const { porcentagemPlaca, porcentagemSang } = calculaPorcentagensPeriogramaCompleto(
                  item.periogramaCompleto
                )

                return (
                  <VFlow vSpacing={0}>
                    <HFlow hSpacing={0.25}>
                      <DateTime
                        value={item.atendProfOdonto.atendimentoProfissional.iniciadoEm}
                        format='DD/MM/YYYY'
                        style={css`
                          font-weight: bold;
                        `}
                      />
                      {'|'}
                      <Text>
                        {item.atendProfOdonto.atendimentoProfissional.lotacao.profissional.nome} (
                        {item.atendProfOdonto.atendimentoProfissional.lotacao.cbo.nome})
                      </Text>
                    </HFlow>
                    <HFlow hSpacing={0.25}>
                      <HLabel title='% de sítios com Placa'>{porcentagemPlaca}</HLabel>
                      {'|'}
                      <HLabel title='% de sítios com Sangramento'>{porcentagemSang}</HLabel>
                    </HFlow>
                    {item.observacao && (
                      <Text>
                        <strong>Observações: </strong>
                        {item.observacao}
                      </Text>
                    )}
                  </VFlow>
                )
              }}
              accordionItemBody={(item) => (
                <PeriogramaCompletoTableView
                  values={item.periogramaCompleto}
                  pips={calculaPipsPeriogramaCompleto(item.periogramaCompleto)}
                />
              )}
            />
          </VFlow>
        </ModalBody>
        {hasItemExpanded && (
          <StickyButton containerRef={ref} showsAt={200} kind='normal' size='small' onClick={collapseAccordion}>
            <Icon
              icon='angleUp'
              style={css`
                margin-right: 0.5rem;
              `}
            />
            Fechar histórico
          </StickyButton>
        )}
      </Modal>
    </Fragment>
  )
}

const classes = {
  accordion: css`
    [data-accordion-component='AccordionItem'] {
      border: 0;
    }

    [data-accordion-component='AccordionItemButton'][aria-expanded='true'],
    [data-accordion-component='AccordionItemButton'][aria-expanded='false'] {
      border: 1px solid ${theme.pallete.gray.c80};
      border-radius: 2px;
    }
  `,
}
